import { useEffect } from 'react'

import {
  Box,
  Form,
  Icon,
  Link,
  Radio,
  Text,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { isString } from 'lodash'

import Callout from 'modules/core/components/Callout'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { manageMapping } from 'modules/attendance/components/CustomTimeRanges/Content/Table/Modals/TimeMappingDropdown/utils/manageMappping'
import { useAttendanceSettingWorkCategories } from 'modules/attendance/lib/attendanceSettingWorkCategoriesContext'
import {
  WizardData,
  WizardDataField,
  validateMandatoryFields,
} from 'modules/attendance/lib/forms/utils/CustomTimeRangesFormData'
import { PayrollConceptSelect } from 'modules/compensations/components/PayrollConceptSelectField'
import { useCategorizedPayrollConcepts } from 'modules/compensations/components/PayrollConceptSelectField/CategorizedPayrollConceptUtil'

import PayrollMappings from './PayrollMappings'

const CompensationMethodStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const { hasBankOfHours } = useAttendanceSettingWorkCategories()

  const isPayrollOptionEnabled = data.hasCompensations || data.payrollEnabled

  const handleNext = async () => {
    const mandatoryFields: WizardDataField[] = ['poolType']

    const { invalidFields, dataWithErrors } = validateMandatoryFields(mandatoryFields, data)

    onDataChanged(dataWithErrors)

    track(trackingEvents.timeTracking.newSpecialHourStep3NextPressed, {
      errors: invalidFields.length ? JSON.stringify(invalidFields) : undefined,
      poolType: data.poolType,
      isEdit: data.isEdit,
      id: data.id,
    })

    if (invalidFields.length) {
      return Promise.reject()
    }
  }
  const { navigation } = useNavigation()
  const route = navigation.payrollBundle.fullPath()

  const onPayrollConceptSelected = (payrollConceptId: unknown[] | unknown | null) => {
    if (!payrollConceptId || !isString(payrollConceptId)) {
      return
    }

    onDataChanged({ ...data, payrollConceptId })
  }

  const existingCompensationMapping = data.payrollMappingsCompensations?.find(
    (mapping) => mapping.reimbursableId === data.timeSettingsCustomTimeRangeCategoryId
  )

  const payrollConceptId = data.payrollConceptId || existingCompensationMapping?.payrollConceptId

  useEffect(() => {
    if (!payrollConceptId) return
    onPayrollConceptSelected(payrollConceptId)
  }, [payrollConceptId])

  useOnNext(handleNext)

  const payrollConceptsOptions = useCategorizedPayrollConcepts(data.payrollConcepts ?? [])

  return (
    <Box flexDirection="row" gap="s48" hideScrollBar overflowY="auto" flexGrow>
      <Box gap="s24" width="s400">
        <Box gap="s8">
          <Text.Header2 weight="semibold">
            {i18n.t(
              'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.title'
            )}
          </Text.Header2>
          <Text.Normal>
            {i18n.t(
              'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.description'
            )}
          </Text.Normal>
        </Box>
        <Form padding="s0" notRounded>
          <Box gap="s16">
            <Radio
              name="virtual_timeoff"
              option={{
                label: (
                  <Box flexDirection="row" gap="s2">
                    <Icon.Medium icon="time-off" color="grey800" />
                    <Text size="300" weight="medium" color="grey800">
                      {i18n.t('time_tracking.settings.time_blocks.categories.modals.time_off')}
                    </Text>
                  </Box>
                ),
                value: 'virtual_timeoff',
              }}
              onChange={() => onDataChanged({ ...data, poolType: 'virtual_timeoff' })}
              value={data.poolType}
              description={i18n.t(
                'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.timeoff_description'
              )}
            />
            <Radio
              name="virtual_payroll"
              option={{
                label: (
                  <Box flexDirection="row" gap="s2">
                    <Icon.Medium icon="payroll" color="grey800" />
                    <Text size="300" weight="medium" color="grey800">
                      {i18n.t('time_tracking.settings.time_blocks.categories.modals.payslip')}
                    </Text>
                  </Box>
                ),
                value: 'virtual_payroll',
                disabled: !isPayrollOptionEnabled,
              }}
              onChange={() => onDataChanged({ ...data, poolType: 'virtual_payroll' })}
              value={data.poolType}
              description={i18n.t(
                'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.payroll_description'
              )}
            >
              {data.payrollEnabled && !data.hasCompensations && (
                <PayrollMappings
                  contractsTaxonomies={data.contractTaxonomies}
                  payrollMappingsTaxonomies={data.payrollMappingsTaxonomies}
                  formMappingsTaxonomies={data.formMappingsTaxonomies}
                  timeSettingsCategoryId={data.timeSettingsCustomTimeRangeCategoryId}
                  onChange={({ taxonomyId, legalEntityId, mappingId }) => {
                    //TODO: Remove this once the new compensation module is fully released
                    const formMappingsTaxonomies = manageMapping({
                      taxonomyId,
                      mappingId,
                      timeRangeCategoryId: data.timeSettingsCustomTimeRangeCategoryId,
                      legalEntityId,
                      formMappings: data.formMappingsTaxonomies,
                    })

                    track(trackingEvents.timeTracking.payrollTaxonomyMapped)

                    onDataChanged({ ...data, formMappingsTaxonomies })
                  }}
                />
              )}
              {data.hasCompensations && (
                <PayrollConceptSelect
                  payrollConcepts={payrollConceptsOptions}
                  value={payrollConceptId ? [payrollConceptId] : []}
                  onChange={onPayrollConceptSelected}
                />
              )}
            </Radio>
            <Radio
              name="balance"
              option={{
                label: (
                  <Box flexDirection="row" gap="s2">
                    <Icon.Medium icon="balance" color="grey800" />
                    <Text size="300" weight="medium" color="grey800">
                      {hasBankOfHours
                        ? i18n.t(
                            'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.bank_of_hours_title'
                          )
                        : i18n.t(
                            'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.balance_title'
                          )}
                    </Text>
                  </Box>
                ),
                value: 'balance',
              }}
              onChange={() => onDataChanged({ ...data, poolType: 'balance' })}
              value={data.poolType}
              description={
                hasBankOfHours
                  ? i18n.t(
                      'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.bank_of_hours_description'
                    )
                  : i18n.t(
                      'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.balance_description'
                    )
              }
            />
          </Box>
        </Form>
      </Box>
      <Box justifyContent="center" width="s360">
        {data.poolType == 'virtual_payroll' && !data.payrollEnabled && !data.hasCompensations && (
          <Callout
            type="critical"
            title={i18n.t(
              'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.payroll_not_active_title'
            )}
            description={i18n.t(
              'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.payroll_not_active_description'
            )}
            link={
              <Link.Href href={route} target="_blank">
                {i18n.t(
                  'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.payroll_not_active_link'
                )}
              </Link.Href>
            }
          />
        )}
      </Box>
    </Box>
  )
}

export const compensationMethodStep = (): WizardStep<WizardData> => ({
  title: i18n.t(
    'time_tracking.settings.time_blocks.categories.special_hours.wizard.compensation_method.title'
  ),
  nextText: i18n.t('common.save'),
  render: (props) => <CompensationMethodStep {...props} />,
  isCompleted: (data) => {
    const mandatoryFields: WizardDataField[] = ['poolType']
    const { invalidFields } = validateMandatoryFields(mandatoryFields, data)
    return invalidFields.length === 0
  },
})
